import { create } from 'zustand';

interface SessionState {
  /** True when the session has expired (401 that could not be silently refreshed) */
  isSessionExpired: boolean;
  /**
   * True when the SIGN-IN (the Keycloak refresh token / SSO session) ends soon.
   * Never set just because the short-lived access token is about to run out:
   * that one is renewed ahead of time (see auth/useSessionLifetime.ts).
   */
  isSessionExpiring: boolean;
  /** Minutes remaining before the sign-in ends (for the warning banner) */
  minutesRemaining: number;
  /** When the sign-in ends, epoch seconds on this computer's clock (null = unknown) */
  sessionEndsAt: number | null;
  /** False when renewing cannot push the end back (Keycloak's maximum session length was reached). */
  canExtend: boolean;
  /** True when renewing the sign-in failed for good: the user has to sign in again. */
  renewalFailed: boolean;
  /** The sessionEndsAt the user closed the warning for (it stays closed for that end only). */
  dismissedEndsAt: number | null;

  setSessionExpired: () => void;
  setSessionExpiring: (minutes: number, endsAt?: number | null, canExtend?: boolean) => void;
  clearSessionExpiring: () => void;
  dismissSessionExpiring: () => void;
  setRenewalFailed: () => void;
  resetSession: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  isSessionExpired: false,
  isSessionExpiring: false,
  minutesRemaining: 0,
  sessionEndsAt: null,
  canExtend: true,
  renewalFailed: false,
  dismissedEndsAt: null,

  setSessionExpired: () =>
    set({ isSessionExpired: true, isSessionExpiring: false }),

  setSessionExpiring: (minutes, endsAt = null, canExtend = true) =>
    set({ isSessionExpiring: true, minutesRemaining: minutes, sessionEndsAt: endsAt, canExtend }),

  clearSessionExpiring: () =>
    set({ isSessionExpiring: false, minutesRemaining: 0, sessionEndsAt: null, canExtend: true }),

  // Closing the warning hides it for this end time only. A later end (after a
  // renewal) or an end it could not tell apart (null) behaves as before.
  dismissSessionExpiring: () =>
    set((s) =>
      s.sessionEndsAt === null
        ? { isSessionExpiring: false, minutesRemaining: 0 }
        : { dismissedEndsAt: s.sessionEndsAt },
    ),

  setRenewalFailed: () => set({ renewalFailed: true }),

  resetSession: () =>
    set({
      isSessionExpired: false,
      isSessionExpiring: false,
      minutesRemaining: 0,
      sessionEndsAt: null,
      canExtend: true,
      renewalFailed: false,
      dismissedEndsAt: null,
    }),
}));

/**
 * Called from the axios response interceptor when a 401 cannot be recovered
 * via silent token refresh. Uses a flag to prevent multiple simultaneous triggers.
 */
let sessionExpiredTriggered = false;

export function reportSessionExpired() {
  if (sessionExpiredTriggered) return;
  sessionExpiredTriggered = true;
  useSessionStore.getState().setSessionExpired();
}

export function resetSessionExpiredFlag() {
  sessionExpiredTriggered = false;
}
